import { SceneManager } from "../core/scene_manager.js";
import { InstanceBatch } from "./core/batches/instance_batch.js";
import { VfxErodingBillboardAdditiveBatch, VfxErodingBillboardNormalBatch } from "./core/batches/vfx_eroding_billboard_batch.js";
import { VfxTaskRunner } from "./core/runtime/vfx_task_runner.js";
import { VfxPlanarWaveBatch } from "./core/batches/vfx_planar_wave_batch.js";
import { VfxRadialStreakBatch } from "./core/batches/vfx_radial_streak_batch.js";
import { VfxBallisticStreakBatch } from "./core/batches/vfx_ballistic_streak_batch.js";
import { VfxDissolvingSphereBatch } from "./core/batches/vfx_dissolving_sphere_batch.js";
import { VfxPhysicsParticleBatch } from "./core/batches/vfx_physics_particle_batch.js";
import { VfxShockwaveBatch } from "./core/batches/vfx_shockwave_batch.js";

// Merged / Aliased Batches (types kept for backward compatibility with getBatch callers)
import { VfxFireworkTrailAdditiveBatch, VfxFireworkTrailNormalBatch } from "./effects/fireworks/firework_trail/vfx_firework_trail_base_batch.js";
import { VfxLinearStreakBatch } from "./core/batches/vfx_linear_streak_batch.js";

type BatchConstructor<T extends InstanceBatch<any>> = new ( ...args: any[] ) => T;

export class Vfx3DRuntime
{
  public static current_time: number = 0;

  private static batches: Map<unknown, InstanceBatch<any>> = new Map();

  public static registerAll(): void
  {
    if ( this.batches.size > 0 )
    {
      const unique = new Set( this.batches.values() );
      for ( const batch of unique )
        SceneManager.world.add( batch.getMesh() );
      return;
    }

    // 1. Create the 7 core universal batches
    const eroding_additive  = new VfxErodingBillboardAdditiveBatch();
    const eroding_normal    = new VfxErodingBillboardNormalBatch();
    const ballistic_streak  = new VfxBallisticStreakBatch();
    const radial_streak     = new VfxRadialStreakBatch();
    const planar_wave       = new VfxPlanarWaveBatch();
    const shard             = new VfxPhysicsParticleBatch();
    const dissolving_sphere = new VfxDissolvingSphereBatch();
    const shockwave         = new VfxShockwaveBatch();

    // 2. Register core batches
    this.registerBatch( eroding_additive );
    this.registerBatch( eroding_normal );
    this.registerBatch( ballistic_streak );
    this.registerBatch( radial_streak );
    this.registerBatch( planar_wave );
    this.registerBatch( shard );
    this.registerBatch( dissolving_sphere );
    this.registerBatch( shockwave );

    // 3. Map merged batch constructors to their universal batch instances
    this.batches.set( VfxFireworkTrailAdditiveBatch, eroding_additive );
    this.batches.set( VfxFireworkTrailNormalBatch, eroding_normal );
    this.batches.set( VfxLinearStreakBatch, ballistic_streak );

    // 4. Add unique batch meshes to the scene
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
      SceneManager.world.add( batch.getMesh() );
  }

  public static registerBatch<T extends InstanceBatch<any>>( batch: T ): void
  {
    this.batches.set( batch.constructor, batch );
  }

  public static getBatch<T extends InstanceBatch<any>>( batch_class: BatchConstructor<T> ): T
  {
    const batch = this.batches.get( batch_class );
    if ( !batch )
      throw new Error( `Batch ${batch_class.name} is not registered in VfxRuntime` );

    return batch as T;
  }

  public static update( dt: number, _distance_moved: number = 0 ): void
  {
    this.current_time += dt;
    const now = this.current_time;

    VfxTaskRunner.update( now );

    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
      batch.update( dt, now );

    // Commit all changes to the GPU
    this.render();
  }

  private static render(): void
  {
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
      batch.render();
  }

  public static killAllBatches(): void
  {
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
      batch.killAll();
  }

  public static reset(): void
  {
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
    {
      batch.killAll();
      batch.getMesh().parent?.remove( batch.getMesh() );
    }

    VfxTaskRunner.clear();
    this.current_time = 0;
  }

  public static dispose(): void
  {
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
    {
      batch.killAll();

      const mesh = batch.getMesh();
      mesh.parent?.remove( mesh );
      batch.dispose();
    }

    this.batches.clear();
    VfxTaskRunner.clear();
    this.current_time = 0;
  }

  public static getStats(): IVfx3DRuntimeStats
  {
    const sparks_generic: IVfx3DLayerStats = { active: 0, pending: 0, max: 0 };
    const dissolve: IVfx3DLayerStats       = { active: 0, pending: 0, max: 0 };
    const fireworks: IVfx3DLayerStats      = { active: 0, pending: 0, max: 0 };
    const cannon: IVfx3DLayerStats         = { active: 0, pending: 0, max: 0 };
    const jam_impact: IVfx3DLayerStats     = { active: 0, pending: 0, max: 0 };

    let batches_active    = 0;
    let particles_active  = 0;
    let particles_pending = 0;

    const addStats = ( target: IVfx3DLayerStats, stats: { active: number; pending: number; max: number } ) =>
    {
      target.active  += stats.active;
      target.pending += stats.pending;
      target.max     += stats.max;

      particles_active  += stats.active;
      particles_pending += stats.pending;
      if ( stats.active > 0 || stats.pending > 0 )
        batches_active++;
    };

    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
    {
      const s    = batch.getStats();
      const name = batch.constructor.name;

      if ( name.includes( "Generic" ) || name.includes( "Sparks" ) || name.includes( "Streak" ) )
        addStats( sparks_generic, s );
      else if ( name.includes( "Dissolving" ) )
        addStats( dissolve, s );
      else if ( name.includes( "Firework" ) )
        addStats( fireworks, s );
      else if ( name.includes( "Cannon" ) )
        addStats( cannon, s );
      else
        addStats( jam_impact, s );
    }

    const batches_total = unique.size;

    return {
      tasks_count: VfxTaskRunner.getTasks().length,
      batches_active,
      batches_total,
      particles_active,
      particles_pending,
      particles_total: particles_active + particles_pending,
      sparks_generic,
      dissolve,
      fireworks,
      cannon,
      jam_impact,
    };
  }

  public static getDetailedStats(): Record<string, { active: number; pending: number; max: number }>
  {
    const res: Record<string, { active: number; pending: number; max: number }> = {};
    const unique = new Set( this.batches.values() );
    for ( const batch of unique )
      res[ batch.constructor.name ] = batch.getStats();
    return res;
  }
}

export interface IVfx3DLayerStats
{
  active: number;
  pending: number;
  max: number;
}

export interface IVfx3DRuntimeStats
{
  tasks_count: number;
  batches_active: number;
  batches_total: number;
  particles_active: number;
  particles_pending: number;
  particles_total: number;

  sparks_generic: IVfx3DLayerStats;
  dissolve: IVfx3DLayerStats;
  fireworks: IVfx3DLayerStats;
  cannon: IVfx3DLayerStats;
  jam_impact: IVfx3DLayerStats;
}
